/**
 * A body's pockets must describe a world that can exist — D240's half of
 * D50's containment rules (`content-schema` `containment.ts`).
 *
 * @remarks
 * A thing whose `containedIn` names an ACTOR is carried by that body. Four
 * rules, each D50's or the D31/D47 dead-content law read for a body:
 *
 * - **The body is real.** `containerActorId` names a declared actor — a
 *   typo there would strand the thing in no one's pocket.
 * - **One home.** A carried thing is not also placed in a room, exactly as a
 *   contained thing may not be: two homes is the ambiguity `containedIn`
 *   exists to remove.
 * - **The body stands somewhere.** A body's contents are reached only while
 *   it is PRESENT, so a voice that never stands in any room the player can
 *   reach (`roomsAnActorCanStandIn` is empty — heard from its `audibleFrom`
 *   and never on stage) carries things nobody can ever name.
 * - **No refusal is dead.** A body that carries something is gone through by
 *   `search`, so a `verbNarratives.search` refusal on it could never speak.
 *   (The schema refuses the same key beside `searchNarratives`.)
 *
 * A cycle cannot pass through a body — a body is never contained — so D50's
 * cycle walk needs nothing new; what a compartment on the body holds is an
 * ordinary thing inside a thing, checked there.
 *
 * @packageDocumentation
 */

import { containerActorIdOf, type ContentPackDefinition, roomsAnActorCanStandIn, stageDirectionsOf } from '@orpheus/content-schema';

import { brokenReference } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/**
 * Validate every thing a body carries.
 *
 * @param contentPack - The parsed pack
 * @returns One failure per broken relationship, all collected
 */
export const validateBodyPockets = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const actorsById = new Map(contentPack.actors.map((actor) => [actor.actorId, actor]));
	const placedItemIds = new Set(contentPack.rooms.flatMap((room) => room.itemPlacements));
	const stage = stageDirectionsOf(contentPack);
	const carryingActorIds = new Set<string>();

	contentPack.items.forEach((item, itemIndex) => {
		const carryingActorId = containerActorIdOf(item.containedIn);

		if (carryingActorId === undefined) {
			return;
		}

		const referencePath = `items[${String(itemIndex)}].containedIn.containerActorId`;
		const carryingActor = actorsById.get(carryingActorId);

		if (carryingActor === undefined) {
			failures.push(brokenReference(referencePath, carryingActorId, 'carried by an undeclared actor'));

			return;
		}

		carryingActorIds.add(carryingActorId);

		if (placedItemIds.has(item.itemId)) {
			failures.push(
				brokenReference(`items[${String(itemIndex)}].containedIn`, item.itemId, 'this item is carried by a body AND placed in a room — it cannot have two homes'),
			);
		}

		if (roomsAnActorCanStandIn(carryingActor, stage).length === 0) {
			failures.push(
				brokenReference(
					referencePath,
					carryingActorId,
					'that body never stands in any room — it is only ever heard — so what it carries could never be reached; place it, or move it on stage',
				),
			);
		}
	});

	contentPack.actors.forEach((actor, actorIndex) => {
		if (carryingActorIds.has(actor.actorId) && actor.verbNarratives?.['search'] !== undefined) {
			failures.push(
				brokenReference(
					`actors[${String(actorIndex)}].verbNarratives.search`,
					actor.actorId,
					'this body carries something, so `search` goes through it and this refusal could never speak — move the lines to `searchNarratives`',
				),
			);
		}
	});

	return failures;
};
